/**
 * Zips in the browser, so the backend never has to: unpacking a zip the
 * mentor uploads, and packing several files into one download.
 */

import { Unzip, UnzipInflate, Zip, ZipPassThrough } from "fflate";

const CHUNK_TIMEOUT_MS = 60000;

/**
 * Unpacks a zip File into [{ path, file }], keeping its folders. Folder
 * entries are dropped; junk like __MACOSX is left for the backend to skip.
 */
export async function extractZip(zipFile, onProgress) {
  const entries = [];
  const pending = [];
  const unzip = new Unzip((entry) => {
    if (entry.name.endsWith("/")) return;
    const parts = [];
    pending.push(new Promise((resolve, reject) => {
      entry.ondata = (err, chunk, final) => {
        if (err) { reject(err); return; }
        parts.push(chunk);
        if (final) {
          const name = entry.name.split("/").pop();
          entries.push({ path: entry.name, file: new File(parts, name) });
          resolve();
        }
      };
    }));
    entry.start();
  });
  unzip.register(UnzipInflate);

  const reader = zipFile.stream().getReader();
  let read = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) { unzip.push(new Uint8Array(0), true); break; }
    read += value.length;
    unzip.push(value);
    if (onProgress) onProgress(Math.round((read / zipFile.size) * 100));
  }
  await Promise.all(pending);
  return entries;
}

/** Where the zip is written: straight to disk where the browser allows it. */
async function openSink(zipName) {
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: zipName,
        types: [{ description: "Zip archive", accept: { "application/zip": [".zip"] } }],
      });
      const writable = await handle.createWritable();
      return {
        write: (chunk) => writable.write(chunk),
        close: () => writable.close(),
        abort: () => writable.abort(),
      };
    } catch (error) {
      if (error?.name === "AbortError") return null; // the user cancelled the dialog
    }
  }
  // Other browsers: collect in memory, then save.
  const parts = [];
  return {
    write: async (chunk) => { parts.push(chunk); },
    close: async () => saveBlob(new Blob(parts, { type: "application/zip" }), zipName),
    abort: async () => { parts.length = 0; },
  };
}

export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Starts a download of one signed URL (the URL itself says "attachment"). */
export function downloadUrl(url) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Downloads [{ path, url, size_bytes }] as one zip, file by file, without
 * compressing (most study files are already compressed). `loadFiles` fetches
 * that list; it runs after the save dialog, which browsers only allow right
 * after a click. Returns false when the user cancels the dialog.
 */
export async function downloadAsZip(loadFiles, zipName, onProgress) {
  const sink = await openSink(zipName);
  if (!sink) return false;
  let files;
  try {
    files = await loadFiles();
  } catch (error) {
    await sink.abort().catch(() => {});
    throw error;
  }

  const total = files.reduce((n, f) => n + (f.size_bytes || 0), 0) || 1;
  let done = 0;
  let writes = Promise.resolve();
  let failed = null;
  const zip = new Zip((err, chunk, final) => {
    if (err) { failed = err; return; }
    writes = writes.then(() => sink.write(chunk));
    if (final) writes = writes.then(() => sink.close());
  });

  try {
    for (const file of files) {
      const response = await fetch(file.url);
      if (!response.ok) throw new Error(`Couldn't download ${file.path} (${response.status})`);
      const entry = new ZipPassThrough(file.path);
      zip.add(entry);
      const reader = response.body.getReader();
      for (;;) {
        const { done: finished, value } = await withTimeout(reader.read(), file.path);
        if (finished) { entry.push(new Uint8Array(0), true); break; }
        entry.push(value);
        done += value.length;
        if (onProgress) onProgress(Math.min(99, Math.round((done / total) * 100)));
        await writes; // keep memory flat when writing to disk
      }
      if (failed) throw failed;
    }
    zip.end();
    await writes;
    if (onProgress) onProgress(100);
    return true;
  } catch (error) {
    zip.terminate();
    await writes.catch(() => {});
    await sink.abort().catch(() => {});
    throw error;
  }
}

function withTimeout(promise, path) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(`Download of ${path} stalled`)), CHUNK_TIMEOUT_MS);
    }),
  ]).finally(() => clearTimeout(timer));
}
